import { Paper, List, ListItem, ListItemText, Box, Tooltip } from '@material-ui/core';
import React, { useCallback, useEffect } from 'react';
import { FC, useState } from 'react';
import { FINAME_ID, FINANCIMENTO_INDIRETO_ID } from 'src/constants';
import { Formater, LocalStorage, TypeRecords } from 'src/framework/commons';
import { FullWidthButton } from 'src/framework/components/buttons';
import { DropZone } from 'src/framework/components/commons/DropZone';
import { InputComboBoxList, InputCurrency, InputMoney } from 'src/framework/components/forms';
import Image from 'material-ui-image'
import api, { IDataRequest, IDataResponse, STATUS_CODE } from 'src/framework/providers/api';
import { notifierStore } from 'src/framework/store';
import { useObservable } from 'src/framework/store/storeUtil';
import cartasCreditoProvider from 'src/providers/pagamentos/CartaCreditoProvider';
import { CacheType, bancos$, documentosPagamentos$, qrCodeFINAME$ } from 'src/store/global.store';
import theme from 'src/theme/light';
import { DocumentosPagamentosType, ErrorValidate, SITUACAO_FINANCIAMENTOS_UPLOADS, TIPO_PESSOA_DOCUMENTO } from 'src/types';
import './styles.scss';
import moment from 'moment';

const MAX_FILE_SIZE_IN_MB = 10

interface FinanciamentoIndiretoProperties {
    checkout: boolean;
    validarArquivos?: boolean;
    formaPagamentoId: number;
    onConfirmar: (value: any) => void;
    codigoVenda: string;
    cupomDesconto: string;
    onClose?: () => void;
    valorPedido?: number;
    pagamentoId: number;
    solo: boolean;
    disabledConfirmar?: boolean;
    disableValorPagamento?: boolean;
    onChangeValue?: (value: any) => void;
    exibirBotoes: boolean;
    documentosTeste?: any[]
}

interface ErrorState {
    banco: ErrorValidate
    documentos: ErrorValidate
}

const INITIAL_ERROR_STATE: ErrorState = {
    documentos: {
        hasError: false,
        hintText: ""
    },
    banco: {
        hasError: false,
        hintText: ""
    },
}

const FinanciamentoIndireto: FC<FinanciamentoIndiretoProperties> = ({
    checkout,
    formaPagamentoId,
    onConfirmar,
    codigoVenda,
    cupomDesconto,
    onClose,
    valorPedido,
    pagamentoId,
    solo,
    disabledConfirmar = false,
    disableValorPagamento = false,
    onChangeValue,
    exibirBotoes,
    documentosTeste,
    validarArquivos = false,
}) => {

    const [documentos, setDocumentos] = useState<DocumentosPagamentosType[]>();
    const [arquivosSelecionados, setArquivosSelecionados] = useState<any[]>([]);

    const documentosPagamento = useObservable(documentosPagamentos$);
    const urlQrCodeCache = useObservable(qrCodeFINAME$);
    const bancos = useObservable(bancos$);
    const [bancoState, setBancoState] = useState<string>("");
    const [bancosPagamentos, setBancosPagamentos] = useState<any[]>([]);
    const [disabled, setDisabled] = useState<boolean>(!checkout);
    const [valorFinanciamento, setValorFinanciamento] = useState<number>(valorPedido || 0);

    const [errorState, setErrorState] = React.useState<ErrorState>(INITIAL_ERROR_STATE);

    const uploadUrl: any = LocalStorage.get<CacheType>("solar.uploadInfo", true);

    const buildDescricaoArequivo = (arquivo: string | undefined, tamanho: string | undefined) => {
        if (arquivo) {
            return arquivo + " - " + tamanho;
        }

        return "";
    }

    const buildExtensoesDocumentos = (extensoes: string | { text: string, value: string }[] | undefined) => {
        if (extensoes) {
            if (typeof extensoes === "string") {
                const extensoesParse = [...extensoes];

                const formato = extensoesParse.map((e: any) => {
                    return e.text;
                });

                return "Formatos Suportados: " + formato;
            } else {
                return "Formatos Suportados: " + extensoes.map(rec => rec.text).join("; ");
            }
        }
    }

    const buildExtensoesDocumentosValues = (extensoes: string | { text: string, value: string }[] | undefined): string[] | undefined => {
        if (extensoes) {
            if (typeof extensoes === "string") {
                const extensoesParse = [...extensoes];
                return extensoesParse.map((rec: any) => rec.value);
            } else {
                return extensoes.map(rec => rec.value);
            }
        }
    }

    const saveFile = (arquivos: any[]) => {
        if (validarArquivos) {
            const arquivoExcedeLimite = arquivos.some(arquivo => {
                const sizeInMb = arquivo.arquivo.size / 1024 ** 2
                return sizeInMb > MAX_FILE_SIZE_IN_MB
            })

            if (arquivoExcedeLimite) {
                notifierStore.setNotifier({
                    content: [
                        `O tamanho do arquivo é maior que o permitido.`,
                        `Tamanho máximo permitido: ${MAX_FILE_SIZE_IN_MB}MB.`
                    ],
                    title: "Aviso",
                    onClose: () => notifierStore.setOpen(false),
                    timeOut: 15_000
                })
                notifierStore.setOpen(true)
                return;
            }
        }

        for (let arquivo of arquivos) {
            arquivosSelecionados?.push(arquivo);

            if (documentos) {
                const idx = documentos.findIndex((d: DocumentosPagamentosType) => d.id === arquivo.keyRow);
                if (idx > -1) {
                    documentos[idx].arquivoSelecionado = arquivo.nome;
                    documentos[idx].tamanhoArquivo = arquivo.tamanho;
                    setDocumentos([...documentos])
                    setDisabled(false);
                }
            }
        }
        setDisabled(false);
        prepareOnChange(valorFinanciamento || 0, bancoState);
    }

    const prepareOnChange = async (valorFinanciamento: number, banco: string) => {
        if (documentos) {
            for (let file of arquivosSelecionados) {
                const idx = documentos.findIndex((d: DocumentosPagamentosType) => d.id === file.keyRow);

                const arquivoSplit = file.arquivo.name.split(".");
                let arquivo = documentos[idx].sufixo_arquivo + "." + arquivoSplit[arquivoSplit.length - 1];

                if (documentos[idx].origem) {
                    arquivo = documentos[idx].origem?.toLocaleLowerCase() + "_" + documentos[idx].id?.toString() + "." + arquivoSplit[arquivoSplit.length - 1];
                }

                const form = new FormData();
                form.append(arquivo, file.arquivo, file.arquivo.name);

                if (idx > -1) {
                    documentos[idx].formData = form;
                    setDocumentos([...documentos])
                }
            }
        }

        if (onChangeValue) {
            onChangeValue({
                banco: banco,
                valor: Formater.toString(valorFinanciamento, TypeRecords.DECIMAL2).replace(",", "."),
                documentos: documentos?.map((row: DocumentosPagamentosType) => {
                    if (row.arquivoSelecionado && row.arquivoSelecionado !== "" && row.situacao !== SITUACAO_FINANCIAMENTOS_UPLOADS.PREENCHIDO) {

                        return row;
                    }
                }).filter((documento) => !!documento),
            });
        }
    }

    const saveArquivos = async () => {
        const url: string = `/pagamentos/cartas_credito/uploads/` + codigoVenda;

        let tudoCerto = true;

        if (arquivosSelecionados) {
            if (documentos) {
                for (let file of arquivosSelecionados) {
                    const idx = documentos.findIndex((d: DocumentosPagamentosType) => d.id === file.keyRow);

                    const arquivoSplit = file.arquivo.name.split(".");
                    const arquivo = documentos[idx].sufixo_arquivo + "." + arquivoSplit[arquivoSplit.length - 1];

                    const form = new FormData();
                    form.append(arquivo, file.arquivo, file.arquivo.name);
                    const request: IDataRequest = {
                        url: url,
                        data: form,
                    }

                    const response = await api.post(request);
                    if (response.statusCode !== STATUS_CODE.OK) {
                        tudoCerto = false;

                        return;
                    }

                    if (idx > -1) {
                        documentos[idx].url = response.data[arquivo].path
                        documentos[idx].data_upload = moment().format("YYYY-MM-DD HH:mm:ss")
                        setDocumentos([...documentos])
                    }
                }

                if (tudoCerto) {
                    setArquivosSelecionados([]);

                    const cartaCredito = {
                        banco: bancoState,
                        valor: Formater.toString(valorFinanciamento, TypeRecords.DECIMAL2).replace(",", "."),
                        carta: documentos?.map((row: DocumentosPagamentosType) => {
                            if (row.arquivoSelecionado && row.arquivoSelecionado !== "") {

                                return {
                                    documento_pagamento_id: row.id,
                                    url: row.url,
                                    data_upload: moment().format("YYYY-MM-DD HH:mm:ss")
                                }
                            }
                        }).filter((documento) => !!documento),
                    }

                    if (checkout) {
                        if (documentosPagamento) {

                            const data: any[] = [];

                            documentos.map((row: DocumentosPagamentosType) => {
                                row.arquivoSelecionado = "";
                                row.tamanhoArquivo = "";

                                data.push(row);
                            });

                            documentosPagamento.data = data;

                            setDocumentos(documentosPagamento.data);
                        }
                    }

                    onConfirmar(cartaCredito);
                    //setDocumentos({ ...documentosPagamento.data });
                }
            }
        }
    }

    const validacao = (): boolean => {
        const errorMessages: string[] = []
        const errors = { ...errorState }

        if ((!bancoState || bancoState === "") && checkout) {
            errorMessages.push(`O campo "Banco" é obrigatório.`)
            errors.banco = { hasError: true, hintText: "" }
        }

        if (documentos) {
            let validaUpload = true;
            /*
            if (cupomDesconto === "") {
                documentos.forEach((row: DocumentosPagamentosType) => {
                    if (formaPagamentoId === row.forma_pagamento_id) {
                        if (checkout && row.checkout) {
                            if (!row.arquivoSelecionado) {
                                validaUpload = false;
                            }
                        }
                    }
                });
            }
            */

            if (!validaUpload) {
                errorMessages.push(`É obrigatorio informar os documentos solicitados.`)
                errors.documentos = { hasError: true, hintText: "" }
            }


        }


        if (errorMessages.length > 0) {
            setErrorState({ ...errors })
            notifierStore.setNotifier({
                title: "Aviso",
                content: errorMessages,
                timeOut: 3500,
                onClose: () => {
                    notifierStore.setOpen(false)
                }
            })
            notifierStore.setOpen(true)

            return false
        }

        return true;
    }

    const init = async () => {
        if (checkout) {
            if (documentosPagamento) {
                setDocumentos(documentosPagamento.data);
            } else {
                setDocumentos([]);
            }
        } else {
            if (pagamentoId && !documentosTeste) {
                const response: IDataResponse = await cartasCreditoProvider.listCartaCredito(pagamentoId || 0);

                if (response.statusCode === STATUS_CODE.OK) {
                    setDocumentos(response.data);
                }
            } else {
                setDocumentos(documentosTeste || []);
            }
        }
    }

    useEffect(() => {
        setDocumentos([...[]]);
        setArquivosSelecionados([...[]]);
        
        return () => {
            setDocumentos([...[]]);
            setArquivosSelecionados([...[]]);
        }
    }, [])

    useEffect(() => {
        init();
    }, [documentosPagamento, documentosTeste])

    useEffect(() => {
        if (bancos) {
            setBancosPagamentos(bancos.data);
        }
    }, [bancos])

    useEffect(() => {
        setValorFinanciamento(valorPedido || 0);
    }, [valorPedido])
    
    const resolveUrlQrCode = useCallback(() : string => {
        return uploadUrl ? `http://${uploadUrl.data?.url + `${uploadUrl.data?.port !== "80" ? "" : ":" + uploadUrl.data?.port}` + urlQrCodeCache?.data?.valor}` : ""
    },[urlQrCodeCache, uploadUrl])

    return <>
        <div className='row FinanciamentoIndireto'>
            <div className={`X12 ${formaPagamentoId === FINAME_ID ? "S6" : ""}`}>
                {checkout && <>
                    <div className="row">
                        <div className={`X12 ${formaPagamentoId !== FINAME_ID ? "S6" : ""}`}>
                            <InputMoney
                                label="Valor a financiar"
                                name="valor-financiamento"
                                value={valorFinanciamento}
                                disabled={solo || disableValorPagamento}
                                onChangeValue={(newValue) => {
                                    const { numberValue } = newValue
                                    setValorFinanciamento(numberValue || 0);
                                    prepareOnChange(numberValue || 0, bancoState);
                                }}

                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className={`X12 ${formaPagamentoId !== FINAME_ID ? "S6" : ""}`}>
                            <InputComboBoxList
                                label="Bancos"
                                textField="nome"
                                valueField="id"
                                options={bancosPagamentos}
                                error={errorState.banco.hasError}
                                value={bancosPagamentos.find((b: any) => b.id === bancoState)}
                                renderOptions={(option: any) => {
                                    return `${option.id} - ${option.nome}`
                                }}
                                renderOptionsLabel={(option: any) => {
                                    return `${option.id} - ${option.nome}`
                                }}
                                onChange={(e) => {
                                    if (e) {
                                        setBancoState(e.id);

                                        prepareOnChange(valorFinanciamento, e.id);
                                    }
                                }}
                            />
                        </div>
                    </div>
                </>}
                <div className="row">
                    {documentos?.map((row: DocumentosPagamentosType) => {

                        if ([FINANCIMENTO_INDIRETO_ID, FINAME_ID].includes(formaPagamentoId)) {
                            if ((checkout && !row.checkout) || (row.situacao && (row.situacao !== "pendente" && row.situacao !== "revisao"))) {
                                return <></>
                            }
                        }

                        return <>
                            <div className={`X12 Paper ${formaPagamentoId !== FINAME_ID ? "S6" : ""}`}>
                                <Paper style={{ minHeight: "180px" }}>
                                    <div className="row" style={{ minHeight: "180px" }}>
                                        <div className="X12 M8">
                                            <List>
                                                <ListItem alignItems="flex-start">
                                                    <ListItemText
                                                        primary={row.titulo}
                                                        secondary={row.descricao}
                                                    />
                                                </ListItem>

                                                <ListItem alignItems="flex-start">
                                                    <ListItemText
                                                        secondary={
                                                            <React.Fragment>
                                                                <div className="row">
                                                                    <div className="X12" style={{ display: "flex", alignItems: "center" }}>
                                                                        <div>
                                                                            <span className="material-icons">file_upload</span>
                                                                        </div>
                                                                        {row.situacao && row.situacao !== SITUACAO_FINANCIAMENTOS_UPLOADS.PENDENTE && row.situacao !== SITUACAO_FINANCIAMENTOS_UPLOADS.REVISAO ?
                                                                            <><div style={{ color: theme.palette.primary.main }}>Documento já enviado</div></> :
                                                                            <><div>Arquivo: {buildDescricaoArequivo(row.arquivoSelecionado, row.tamanhoArquivo)}</div></>}
                                                                    </div>
                                                                    <div className="X12">
                                                                        {buildExtensoesDocumentos(row.extensoes)}
                                                                    </div>
                                                                    {validarArquivos && <div className="X12">
                                                                        <span>Tamanho máximo permitido: {MAX_FILE_SIZE_IN_MB}MB</span>
                                                                    </div>}
                                                                </div>
                                                            </React.Fragment>}
                                                    />
                                                </ListItem>
                                            </List>
                                        </div>
                                        <div className="X12 M4">
                                            <Tooltip title={row?.situacao === SITUACAO_FINANCIAMENTOS_UPLOADS.APROVADO ? "Não é possível reenviar esse documento, pois ele já está aprovado" : ""}>
                                                <div className="Upload">
                                                    <div className="UploadDropZone">
                                                        <DropZone
                                                            multiple={false}
                                                            onChange={saveFile}
                                                            formatosAceitos={buildExtensoesDocumentosValues(row.extensoes)}
                                                            disabled={row?.situacao === SITUACAO_FINANCIAMENTOS_UPLOADS.APROVADO}
                                                            keyRow={row.id} 
                                                        />
                                                    </div>
                                                </div>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </Paper>
                            </div>
                        </>

                    })}
                </div>

            </div>
            <div className={`X12 ${formaPagamentoId === FINAME_ID ? "S6" : ""}`}>
                {formaPagamentoId === FINAME_ID && <>
                    <div className="row X--center X-middle">
                        <div className="X12 S6 M4">
                            <Image
                                width={"150px"} 
                                className="imagem-qr-code"
                                src={resolveUrlQrCode() || ""}
                            />
                            <Box>
                                <strong>
                                Escaneie o QR Code para entender mais sobre o FINAME.
                                </strong>
                            </Box>
                        </div>
                        
                    </div>
                </>}

            </div>
            <div className={`X12`}>
                <div className="row actions">
                    {exibirBotoes ? (<>
                        {onClose ?
                            (<>
                                <div className="X12 S6 M4 L2 M--offset4 L--offset8 X--order2 M--order1">
                                    <FullWidthButton
                                        text="Cancelar"
                                        onClick={() => {
                                            if (onClose) {
                                                onClose();
                                            }
                                        }}
                                    />
                                </div>
                            </>) : ""}
                        <div className={`X12 S6 M4 L2 ${!onClose ? "S--offset6 M--offset8 L--offset10 " : ""}X--order1 M--order2`}>
                            <FullWidthButton
                                text="Confirmar"
                                variant="contained"
                                color="primary"
                                disableElevation
                                disabled={disabled || disabledConfirmar}
                                onClick={() => {

                                    if (!validacao()) {
                                        return;
                                    }

                                    saveArquivos();

                                }}
                            />
                        </div>
                    </>) : ""}
                </div>
            </div>
        </div>
    </>
}

export { FinanciamentoIndireto }
